import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Site } from '../lib/supabase';
import { SiteForm } from '../components/forms/SiteForm';
import { Plus, CreditCard as Edit, Archive, Building2, AlertTriangle, Eye, ArrowRight } from 'lucide-react';

export const SitesPage: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [siteToArchive, setSiteToArchive] = useState<Site | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (siteData: Partial<Site>, projectStatusData?: any) => {
    setSubmitting(true);
    try {
      if (editingSite) {
        const { error } = await supabase
          .from('sites')
          .update(siteData)
          .eq('id', editingSite.id);
        
        if (error) throw error;
        
        // Update project status for existing site
        if (projectStatusData) {
          const { error: statusError } = await supabase
            .from('project_status')
            .upsert([{
              site_id: editingSite.id,
             ...projectStatusData,
             hygienist_id: projectStatusData.hygienist_id || null
            }], { onConflict: 'site_id' });
          
          if (statusError) {
            console.error('Error updating project status:', statusError);
          }
        }
      } else {
        const { data: newSite, error } = await supabase
          .from('sites')
          .insert([siteData])
          .select()
          .single();
        
        if (error) throw error;

        // Always create project status for new site
        if (newSite) {
          // Default values for project status
          const defaultProjectStatus = {
            gas_approval: 'live',
            electrical_approval: 'live',
            water_approval: 'live',
            sewer_approval: 'live',
            council_approval: 'pending',
            epa_permit: 'not_required',
            safework_notification: 'pending',
            temporary_fence: 'pending',
            air_monitoring: 'not_required',
            soil_testing: 'not_required',
            hygienist_required: false,
            gas_approval_notes: '',
            electrical_approval_notes: '',
            water_approval_notes: '',
            sewer_approval_notes: '',
            council_approval_notes: '',
            epa_permit_notes: '',
            safework_notification_notes: '',
            temporary_fence_notes: '',
            air_monitoring_notes: '',
            soil_testing_notes: ''
          };

          const projectStatusToCreate = {
            site_id: newSite.id,
            ...defaultProjectStatus,
            // Override with form data if provided
            ...(projectStatusData || {}),
            hygienist_id: projectStatusData?.hygienist_id || null,
            hygienist_required: !!projectStatusData?.hygienist_id
          };

          const { error: statusError } = await supabase
            .from('project_status')
            .insert([projectStatusToCreate]);

          if (statusError) {
            console.error('Error creating project status:', statusError);
          }
        }
      }
      
      setShowModal(false);
      setEditingSite(null);
      fetchSites();
    } catch (error) {
      console.error('Error saving site:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!siteToArchive) return;
    
    setArchiving(true);
    try {
      const { error } = await supabase.rpc('archive_site', {
        site_id_param: siteToArchive.id,
        reason_param: archiveReason || 'Site archived'
      });
      
      if (error) throw error;
      
      setShowArchiveModal(false);
      setSiteToArchive(null);
      setArchiveReason('');
      fetchSites();
    } catch (error) {
      console.error('Error archiving site:', error);
      alert('Failed to archive site. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Site Management</h1>
        <button
          onClick={() => {
            setEditingSite(null);
            setShowModal(true);
          }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add New Site</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map((site) => (
          <Link 
            key={site.id} 
            to={`/sites/${site.id}`}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-orange-300 transition-all duration-200 block group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{site.name}</h3>
                  <p className="text-sm text-gray-500">{site.address}</p>
                  <p className="text-xs text-gray-400 capitalize">{site.type.replace('_', ' ')}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    event?.preventDefault();
                    setEditingSite(site);
                    setShowModal(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button 
                  onClick={(event) => {
                    event.preventDefault();
                    setSiteToArchive(site);
                    setShowArchiveModal(true);
                  }}
                 className="p-1 text-gray-400 hover:text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"
                 title="Archive Site"
                >
                 <Archive className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(site.status)}`}>
                  {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Start Date</span>
                <span className="text-sm text-gray-600">
                  {new Date(site.start_date).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">End Date</span>
                <span className="text-sm text-gray-600">
                  {new Date(site.end_date).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Asbestos Type</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  site.asbestos_type === 'friable' ? 'bg-red-100 text-red-800' :
                  site.asbestos_type === 'non_friable' ? 'text-white' :
                  site.asbestos_type === 'mixed' ? 'bg-blue-100 text-blue-800' :
                  site.asbestos_type === 'unknown' ? 'bg-purple-100 text-purple-800' :
                  'bg-green-100 text-green-800'
                }`} style={site.asbestos_type === 'non_friable' ? { backgroundColor: '#f59e0b' } : {}}>
                  {site.asbestos_type === 'non_friable' ? 'Non-Friable' :
                   site.asbestos_type === 'mixed' ? 'Mixed' :
                   site.asbestos_type === 'unknown' ? 'Unknown' :
                   site.asbestos_type?.charAt(0).toUpperCase() + site.asbestos_type?.slice(1) || 'None'}
                </span>
              </div>

              {site.requirements && site.requirements.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm font-medium text-gray-700">Requirements:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {site.requirements.slice(0, 3).map((req, index) => (
                      <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {req}
                      </span>
                    ))}
                    {site.requirements.length > 3 && (
                      <span className="text-xs text-gray-500">+{site.requirements.length - 3} more</span>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-orange-600 font-medium">View Details</span>
                <ArrowRight className="h-4 w-4 text-orange-600 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {sites.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sites found</h3>
          <p className="text-gray-500 mb-4">Get started by adding your first demolition site.</p>
          <button
            onClick={() => {
              setEditingSite(null);
              setShowModal(true);
            }}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Add New Site
          </button>
        </div>
      )}

      {showModal && (
        <SiteForm
          site={editingSite}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingSite(null);
          }}
          loading={submitting}
        />
      )}

      {/* Archive Modal */}
      {showArchiveModal && siteToArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Archive className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Archive Site</h3>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to archive <strong>{siteToArchive.name}</strong>? 
                The site will be moved to the archived sites section and can be restored later.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for archiving (optional)
                </label>
                <textarea
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="e.g., Project completed, Site no longer active..."
                />
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> Archiving will preserve all site data, inductions, and worker assignments. 
                  The site can be restored from the Archived Sites page.
                </p>
              </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowArchiveModal(false);
                    setSiteToArchive(null);
                    setArchiveReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchive}
                  disabled={archiving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {archiving ? 'Archiving...' : 'Archive Site'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};